/**
 * Node Registry - Centralized Node Registration System
 * 
 * This file provides a SINGLE SOURCE OF TRUTH for node registration.
 * Instead of manually updating 13+ files when adding a new node,
 * define everything here and use the registry throughout the app.
 * 
 * USAGE:
 * 1. Create your node definition in nodeDefinitions.ts
 * 2. Add an entry to NODE_REGISTRY below with all metadata
 * 3. That's it! The rest is automatic.
 */

import type { UniversalNodeDefinition } from '../components/nodes';
import { NODE_DEFINITIONS } from './nodeDefinitions';
import { SYSTEM_PROMPTS } from '../constants/app';

// Import icons
import CreateIcon from '@mui/icons-material/Create';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import OutputIcon from '@mui/icons-material/Output';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import BrushIcon from '@mui/icons-material/Brush';
import MicIcon from '@mui/icons-material/Mic';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import MusicNoteIcon from '@mui/icons-material/MusicNote';

/**
 * Handler types for node execution
 */
export type NodeHandlerType =
  | 'text'           // handleSubmit - Text generation
  | 'claude'         // handleClaudeSubmit - Claude text generation
  | 'kimi'           // handleKimiSubmit - Kimi text generation via Ollama Cloud
  | 'qwen'           // handleQwenSubmit - Qwen text generation via Ollama Cloud
  | 'minimax'        // handleMinimaxSubmit - MiniMax text generation via Ollama Cloud
  | 'geminiText'     // handleGeminiTextSubmit - Gemini text generation via Google AI direct
  | 'grokText'       // handleGrokTextSubmit - Grok text generation via xAI direct
  | 'image'          // handleImageGenerate - OpenAI/Gemini image generation
  | 'fluxImage'      // handleFluxImageGenerate - FLUX image generation
  | 'falAIImage'     // handleFalAIImageGenerate - fal.ai image generation
  | 'falAIVideo'     // handleFalAIVideoGenerate - fal.ai video generation
  | 'xaiImage'       // handleXAIImageGenerate - xAI Grok image via direct API
  | 'xaiVideo'       // handleXAIVideoGenerate - xAI Grok video via direct API
  // No 'piapi': Seedance 2 was the only node on it and moved to fal.ai
  // (handlerType 'falAIVideo') in migration 113. The PiAPI HTTP route and
  // service still exist under /api/piapi, but nothing dispatches on a 'piapi'
  // handler type any more, so it is not a member of this union.
  | 'imageProcess'   // handleImageProcess - Image resize/crop/AI-enhance
  | 'videoProcess'   // handleVideoProcess - Video trim/split/scale
  | 'heygenVideo'    // handleHeygenVideoGenerate - HeyGen avatar/translate via fal.ai
  | 'wanAnimate'     // handleWanAnimateGenerate - Wan Animate video-to-video
  | 'video'          // handleVideoGenerate - Vertex AI video generation
  | 'audioGen'       // handleAudioGenerate - ElevenLabs TTS / SFX / Music (audio output)
  | 'dictation'      // handleDictationSubmit - Audio transcription
  | 'none';          // No handler (containers, outputs)

/**
 * Node category for palette organization
 */
export type NodeCategory =
  | 'text'           // Text generation nodes
  | 'image'          // Image generation nodes
  | 'video'          // Video generation nodes
  | 'audio'          // Audio generation nodes (TTS / SFX / Music)
  | 'container'      // Container/panel nodes
  | 'utility';       // Utility nodes

/**
 * Extended node registration with all metadata
 * This is the SINGLE place to define everything about a node
 */
export interface NodeRegistration {
  // Core identity (must match nodeDefinitions.ts)
  type: string;                        // PascalCase type name (e.g., 'GPTImage')
  camelCaseType: string;               // camelCase for React Flow (e.g., 'gptImage')
  
  // Definition reference
  definition: UniversalNodeDefinition;
  
  // Palette/Menu configuration
  category: NodeCategory;              // Which section in palette/menu
  icon: React.ComponentType<{ style?: React.CSSProperties }>;  // MUI icon component (supports style prop)
  logoPath?: string;                   // Path to model logo image (optional)
  description: string;                 // Tooltip description
  hidden?: boolean;                    // Hide from palette/menu
  
  // Handler configuration
  handlerType: NodeHandlerType;        // Which handler to use for execution
  
  // Connection configuration
  hasMultipleInputHandles?: boolean;   // Show TEXT + IMAGE handles (auto-derived if not set)
  
  // Sizing (optional - uses defaults if not specified).
  // Both fall back to 448 in getNodeDimensions(); there is no "auto" — an
  // unsized node is unbounded by CSS and measures at the CANVAS width, which is
  // how flows got saved with 2560px-wide nodes. Adding an override here means
  // mirroring it in server/config/nodeDimensions.ts in the same commit
  // (src/config/__tests__/serverNodeDimensions.test.ts enforces that).
  defaultWidth?: number;               // Default: 448
  defaultHeight?: number;              // Default: 448
  
  // Initial data for new nodes
  initialData?: Record<string, unknown>;
}

/**
 * NODE REGISTRY - THE SINGLE SOURCE OF TRUTH
 * 
 * Add new nodes here. Everything else is derived from this.
 */
export const NODE_REGISTRY: Record<string, NodeRegistration> = {
  // ============ TEXT NODES ============
  'StartingPrompt': {
    type: 'StartingPrompt',
    camelCaseType: 'startingPrompt',
    definition: NODE_DEFINITIONS['StartingPrompt'],
    category: 'text',
    icon: CreateIcon,
    description: 'Start a creative flow with an initial prompt',
    hidden: true, // Use Writer instead
    handlerType: 'text',
    initialData: { prompt: '', systemPrompt: SYSTEM_PROMPTS.DEFAULT },
  },

  'AgentPrompt': {
    type: 'AgentPrompt',
    camelCaseType: 'agentPrompt',
    definition: NODE_DEFINITIONS['AgentPrompt'],
    category: 'text',
    icon: SmartToyIcon,
    logoPath: '/icons/models/openai.png',
    description: 'Chat with GPT models to write stories, scripts, prompts, or any text. Connect multiple inputs for context-aware responses.',
    hidden: true, // Use ChatGPT instead
    handlerType: 'text',
    hasMultipleInputHandles: true,
    initialData: { prompt: '', selectedModel: 'gpt-5.4-nano', systemPrompt: SYSTEM_PROMPTS.DEFAULT },
  },
  
  'ChatGPT': {
    type: 'ChatGPT',
    camelCaseType: 'chatGPT',
    definition: NODE_DEFINITIONS['ChatGPT'],
    category: 'text',
    icon: SmartToyIcon,
    logoPath: '/icons/models/openai.png',
    description: 'Chat with ChatGPT models to write stories, scripts, prompts, or any text. Connect multiple inputs for context-aware responses.',
    handlerType: 'text',
    hasMultipleInputHandles: true,
    initialData: { prompt: '', selectedModel: 'gpt-5.4-nano' },
  },
  
  'Claude': {
    type: 'Claude',
    camelCaseType: 'claude',
    definition: NODE_DEFINITIONS['Claude'],
    category: 'text',
    icon: SmartToyIcon,
    logoPath: '/icons/models/claude.png',
    description: 'Chat with Claude models for creative writing, analysis, and complex reasoning. Supports images and multiple inputs.',
    handlerType: 'claude',
    hasMultipleInputHandles: true,
    initialData: { prompt: '', selectedModel: 'claude-haiku-4-5' },
  },
  
  'Kimi': {
    type: 'Kimi',
    camelCaseType: 'kimi',
    definition: NODE_DEFINITIONS['Kimi'],
    category: 'text',
    icon: SmartToyIcon,
    logoPath: '/icons/models/kimi.svg',
    description: 'Chat with Kimi K2.6 by Moonshot AI (via Ollama Cloud). Multimodal, 256K context, excellent at reasoning, coding, and agentic tasks.',
    handlerType: 'kimi',
    hasMultipleInputHandles: true,
    initialData: { prompt: '', selectedModel: 'kimi-k2.6' },
  },

  'Qwen': {
    type: 'Qwen',
    camelCaseType: 'qwen',
    definition: NODE_DEFINITIONS['Qwen'],
    category: 'text',
    icon: SmartToyIcon,
    logoPath: '/icons/models/qwen.svg',
    description: 'Chat with Qwen 3.5 by Alibaba (via Ollama Cloud). Multimodal, 256K context, strong multilingual reasoning.',
    handlerType: 'qwen',
    hasMultipleInputHandles: true,
    initialData: { prompt: '', selectedModel: 'qwen-3.5' },
  },

  'Minimax': {
    type: 'Minimax',
    camelCaseType: 'minimax',
    definition: NODE_DEFINITIONS['Minimax'],
    category: 'text',
    icon: SmartToyIcon,
    logoPath: '/icons/models/minimax.svg',
    description: 'Chat with MiniMax M2.7 (via Ollama Cloud). Agent-capable, complex skills, dynamic tool search.',
    handlerType: 'minimax',
    hasMultipleInputHandles: true,
    initialData: { prompt: '', selectedModel: 'minimax-m2.7' },
  },

  'GeminiText': {
    type: 'GeminiText',
    camelCaseType: 'geminiText',
    definition: NODE_DEFINITIONS['GeminiText'],
    category: 'text',
    icon: SmartToyIcon,
    logoPath: '/icons/models/gemini.png',
    description: 'Chat with Google Gemini text models. Includes Gemini 3.5 Flash (most-intelligent), 3.1 Pro Preview, 3 Flash Preview, and 3.1 Flash-Lite (cheapest).',
    handlerType: 'geminiText',
    hasMultipleInputHandles: true,
    initialData: { prompt: '', selectedModel: 'gemini-3.6-flash' },
  },

  'GrokText': {
    type: 'GrokText',
    camelCaseType: 'grokText',
    definition: NODE_DEFINITIONS['GrokText'],
    category: 'text',
    icon: SmartToyIcon,
    logoPath: '/icons/models/grok.svg',
    description: 'Chat with xAI Grok text models. Grok 4.3 (1M context, cheapest), Grok 4.5 (deeper reasoning), and Grok Build (code-focused).',
    handlerType: 'grokText',
    hasMultipleInputHandles: true,
    initialData: { prompt: '', selectedModel: 'grok-4.3' },
  },

  // ============ IMAGE NODES ============
  'ImagePrompt': {
    type: 'ImagePrompt',
    camelCaseType: 'imagePrompt',
    definition: NODE_DEFINITIONS['ImagePrompt'],
    category: 'image',
    icon: ImageIcon,
    logoPath: '/icons/models/gemini.png',
    description: 'Create images with Google Gemini. Great for photorealistic images, illustrations, and concept art with excellent prompt understanding.',
    handlerType: 'image',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'gemini-3.1-flash-image',
      parameters: { aspectRatio: '9:16' }
    },
  },

  'GPTImage': {
    type: 'GPTImage',
    camelCaseType: 'gptImage',
    definition: NODE_DEFINITIONS['GPTImage'],
    category: 'image',
    icon: ImageIcon,
    logoPath: '/icons/models/openai.png',
    description: 'Create images with OpenAI GPT Image. Excels at following complex instructions, text rendering, and coherent compositions.',
    handlerType: 'image',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'gpt-image-2',
      parameters: { aspectRatio: '1:1', quality: 'medium' }
    },
  },
  
  'FluxImage': {
    type: 'FluxImage',
    camelCaseType: 'fluxImage',
    definition: NODE_DEFINITIONS['FluxImage'],
    category: 'image',
    icon: ImageIcon,
    logoPath: '/icons/models/flux.png',
    description: 'Create images with FLUX by Black Forest Labs. Industry-leading quality for portraits, landscapes, and artistic styles.',
    handlerType: 'fluxImage',
    hasMultipleInputHandles: true,
    initialData: { 
      prompt: '', 
      selectedModel: 'flux-2-flash',
      parameters: { aspectRatio: '1:1' }
    },
  },
  
  'ZImageTurbo': {
    type: 'ZImageTurbo',
    camelCaseType: 'zImageTurbo',
    definition: NODE_DEFINITIONS['ZImageTurbo'],
    category: 'image',
    icon: ImageIcon,
    logoPath: '/icons/models/z-image.png',
    description: 'Lightning-fast image generation in under 2 seconds. Perfect for rapid prototyping and exploring ideas quickly.',
    handlerType: 'falAIImage',
    hasMultipleInputHandles: true,
    initialData: { 
      prompt: '', 
      selectedModel: 'z-image-turbo',
      parameters: { aspectRatio: '1:1' }
    },
  },
  
  'QwenImage': {
    type: 'QwenImage',
    camelCaseType: 'qwenImage',
    definition: NODE_DEFINITIONS['QwenImage'],
    category: 'image',
    icon: ImageIcon,
    logoPath: '/icons/models/qwen.png',
    description: 'Create images with Alibaba Qwen. Strong at anime, illustrations, and Asian-inspired aesthetics with vivid colors.',
    handlerType: 'falAIImage',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'qwen-image-2',
      parameters: { aspectRatio: '1:1' }
    },
  },
  
  'QwenImageEdit': {
    type: 'QwenImageEdit',
    camelCaseType: 'qwenImageEdit',
    definition: NODE_DEFINITIONS['QwenImageEdit'],
    category: 'image',
    icon: ImageIcon,
    logoPath: '/icons/models/qwen.png',
    description: 'Edit existing images with text instructions. Change colors, add objects, modify backgrounds, or transform styles.',
    hidden: true,
    handlerType: 'falAIImage',
    hasMultipleInputHandles: true,
    initialData: { 
      prompt: '', 
      selectedModel: 'qwen-image-edit',
      parameters: { aspectRatio: '1:1' }
    },
  },
  
  'Seedream': {
    type: 'Seedream',
    camelCaseType: 'seedream',
    definition: NODE_DEFINITIONS['Seedream'],
    category: 'image',
    icon: ImageIcon,
    logoPath: '/icons/models/seedream.svg',
    description: 'Create images with ByteDance Seedream. Exceptional at dreamy, surreal compositions and creative visual concepts.',
    handlerType: 'falAIImage',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'seedream-5-lite',
      parameters: { aspectRatio: '1:1' }
    },
  },
  
  'RecraftV3': {
    type: 'RecraftV3',
    camelCaseType: 'recraftV3',
    definition: NODE_DEFINITIONS['RecraftV3'],
    category: 'image',
    icon: ImageIcon,
    description: 'Professional design-focused images. Best for logos, icons, UI elements, and clean graphic design work. V4 + V4 Pro selectable.',
    hidden: true,
    handlerType: 'falAIImage',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'recraft-v4',
      parameters: { aspectRatio: '1:1' }
    },
  },

  'GrokImage': {
    type: 'GrokImage',
    camelCaseType: 'grokImage',
    definition: NODE_DEFINITIONS['GrokImage'],
    category: 'image',
    icon: ImageIcon,
    logoPath: '/icons/models/grok.svg',
    description: 'xAI Grok image model. Supports text-to-image and image-to-image editing.',
    handlerType: 'xaiImage',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'grok-image',
      parameters: { aspectRatio: '1:1' }
    },
  },

  'KlingImage': {
    type: 'KlingImage',
    camelCaseType: 'klingImage',
    definition: NODE_DEFINITIONS['KlingImage'],
    category: 'image',
    icon: ImageIcon,
    logoPath: '/icons/models/kling.png',
    description: 'Kuaishou Kling Image O3. Up to 4K with strong prompt adherence, and multi-reference editing with up to 10 reference images.',
    handlerType: 'falAIImage',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'kling-image-o3',
      parameters: { aspectRatio: '1:1', resolution: '1K', outputFormat: 'png' },
    },
  },

  'GLMImage': {
    type: 'GLMImage',
    camelCaseType: 'glmImage',
    definition: NODE_DEFINITIONS['GLMImage'],
    category: 'image',
    icon: ImageIcon,
    description: 'Zhipu GLM-Image — 9B parameter model, excellent at text rendering and typography in images.',
    hidden: true,
    handlerType: 'falAIImage',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'glm-image',
      parameters: { aspectRatio: '1:1' }
    },
  },

  // ============ VIDEO NODES ============
  'VeoVideo': {
    type: 'VeoVideo',
    camelCaseType: 'veoVideo',
    definition: NODE_DEFINITIONS['VeoVideo'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/veo.png',
    description: 'Google\'s flagship video model with native audio generation. Creates cinematic, high-fidelity videos up to 8 seconds.',
    handlerType: 'video',
    hasMultipleInputHandles: true,
    initialData: { 
      prompt: '', 
      selectedModel: 'veo-3.1-fast-generate-preview',
      parameters: {
        aspectRatio: '16:9',
        duration: '8',
        mode: 'image-to-video'  // Default to image-to-video mode
      }
    },
  },

  // Gemini Omni — Google direct video (Gemini API). Clone of VeoVideo but
  // handlerType 'video' routes through the Google/VeoVideoService path (NOT
  // fal.ai). 720p-only, 3–10s, T2V + I2V + first-last-frame.
  'GeminiOmni': {
    type: 'GeminiOmni',
    camelCaseType: 'geminiOmni',
    definition: NODE_DEFINITIONS['GeminiOmni'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/gemini.png',
    description: 'Google Gemini Omni video — 720p text-to-video and image-to-video. Priced like VEO 3.1 Fast.',
    handlerType: 'video',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      // Must stay in step with GEMINI_OMNI_NODE_DEF's selectedModel default: this
      // value is persisted verbatim into every saved flow and beats every `||`
      // fallback downstream (see config/__tests__/nodeRegistryModelIds.test.ts).
      // Pinning the shut-down preview here would make every NEWLY created node
      // dead on arrival after 2026-09-30.
      selectedModel: 'gemini-omni-1.1-flash',
      parameters: {
        aspectRatio: '16:9',
        duration: '5',
        mode: 'image-to-video',
        resolution: '720p'
      }
    },
  },

  // fal.ai Video Nodes
  // Unified Kling node — replaces Kling26Video, Kling25Video, KlingO3Video in the
  // palette. The older three are kept registered (hidden) so existing flows
  // continue to render until DB migration 090 has run.
  'KlingVideo': {
    type: 'KlingVideo',
    camelCaseType: 'klingVideo',
    definition: NODE_DEFINITIONS['KlingVideo'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/kling.png',
    description: 'Kuaishou Kling video generation. Pick 2.5 Turbo (fast), 2.6 Pro (cinematic + audio), or 3.0 Standard/Pro (latest, FFLF support).',
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'kling-3.0-standard',
      parameters: { mode: 'image-to-video', aspectRatio: '16:9', duration: '5', enableAudio: false }
    },
  },

  'Kling26Video': {
    type: 'Kling26Video',
    camelCaseType: 'kling26Video',
    definition: NODE_DEFINITIONS['Kling26Video'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/kling.png',
    description: 'Cinematic videos with built-in audio. Great for dramatic scenes, character animation, and storytelling up to 10 seconds.',
    hidden: true,
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'kling-2.6',
      parameters: { aspectRatio: '16:9', duration: '5' }
    },
  },

  'Kling25Video': {
    type: 'Kling25Video',
    camelCaseType: 'kling25Video',
    definition: NODE_DEFINITIONS['Kling25Video'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/kling.png',
    description: 'Fast Kling video generation at lower cost. Good balance of speed and quality for quick iterations.',
    hidden: true,
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      // 'kling-2.5' is NOT a model id anywhere in this codebase — the real one
      // is 'kling-2.5-turbo' (FAL_VIDEO_MODELS, both cost tables, migration
      // 004/033/054). Because this value is truthy it beat every `||` fallback,
      // so saved flows carrying a Kling25Video node reserved falai:kling-2.5,
      // hit "Model not found" in CreditService.reserve(), and 500'd.
      selectedModel: 'kling-2.5-turbo',
      parameters: { aspectRatio: '16:9', duration: '5' }
    },
  },

  'KlingO3Video': {
    type: 'KlingO3Video',
    camelCaseType: 'klingO3Video',
    definition: NODE_DEFINITIONS['KlingO3Video'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/kling.png',
    description: 'Kling 3.0 — the latest generation with Standard and Pro quality tiers. Cinematic motion, dialogue, and realistic scenes up to 10 seconds.',
    hidden: true,
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: '',
      parameters: { quality: 'standard', aspectRatio: '16:9', duration: '5' }
    },
  },
  
  'WanVideo': {
    type: 'WanVideo',
    camelCaseType: 'wanVideo',
    definition: NODE_DEFINITIONS['WanVideo'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/wan.png',
    description: 'Wan 2.2 (Legacy). Superseded by Wan 2.6/2.7 (use the Wan node). Kept for back-compat with existing flows.',
    hidden: true,
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'wan-2.2',
      parameters: { aspectRatio: '16:9', duration: '5' }
    },
  },

  'Wan26Video': {
    type: 'Wan26Video',
    camelCaseType: 'wan26Video',
    definition: NODE_DEFINITIONS['Wan26Video'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/wan.png',
    description: 'Wan video generation. Defaults to 2.7 (Thinking Mode, native 1080p) with 2.6 still selectable.',
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'wan-2.7',
      parameters: { aspectRatio: '16:9', resolution: '1080p', duration: '5' }
    },
  },
  
  'WanAnimateVideo': {
    type: 'WanAnimateVideo',
    camelCaseType: 'wanAnimateVideo',
    definition: NODE_DEFINITIONS['WanAnimateVideo'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/wan.png',
    description: 'Animate a character image using motion from a reference video. Supports Replace (swap characters) and Move (replicate expressions) modes.',
    handlerType: 'wanAnimate',
    hasMultipleInputHandles: true,
    initialData: { 
      prompt: '', 
      // Same orphan-id class as Kling25Video above: 'wan-animate' is not a
      // model id anywhere — the real ones are 'wan-animate-replace' /
      // 'wan-animate-move' (migrations 030/031/054, creditCostUtils,
      // server nodeRegistry.defaultModel, selectionCostUtils). The browser
      // handler ignores selectedModel entirely and derives the model from the
      // `mode` param, so this only ever bit the HEADLESS path, where
      // FlowExecutionService does `data.selectedModel || reg.defaultModel` and
      // the truthy orphan won. Pinned to the 'replace' default mode to match.
      selectedModel: 'wan-animate-replace',
      parameters: { mode: 'replace', resolution: '480p', useTurbo: 'true' }
    },
  },
  
  'LTXVideo': {
    type: 'LTXVideo',
    camelCaseType: 'ltxVideo',
    definition: NODE_DEFINITIONS['LTXVideo'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/ltx.png',
    description: 'High-quality video with synchronized audio. Best for music videos, ambient scenes, and audio-visual content.',
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'ltx-2.3',
      parameters: { aspectRatio: '16:9', duration: '6', enableAudio: true }
    },
  },
  
  'LTXFastVideo': {
    type: 'LTXFastVideo',
    camelCaseType: 'ltxFastVideo',
    definition: NODE_DEFINITIONS['LTXFastVideo'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/ltx.png',
    description: 'Quick LTX video previews in seconds. Use for testing concepts before committing to full-quality renders.',
    hidden: true,
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'ltx-2.3-fast',
      parameters: { aspectRatio: '16:9', duration: '5' }
    },
  },

  'PixVerseVideo': {
    type: 'PixVerseVideo',
    camelCaseType: 'pixVerseVideo',
    definition: NODE_DEFINITIONS['PixVerseVideo'],
    category: 'video',
    icon: VideocamIcon,
    description: 'PixVerse video — lifelike physics and stylized content. v6 default, v5.5 selectable.',
    hidden: true,
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'pixverse-6',
      parameters: { aspectRatio: '16:9', duration: '5' }
    },
  },

  'HunyuanVideo': {
    type: 'HunyuanVideo',
    camelCaseType: 'hunyuanVideo',
    definition: NODE_DEFINITIONS['HunyuanVideo'],
    category: 'video',
    icon: VideocamIcon,
    description: "Tencent's latest video model (Hunyuan 1.5)",
    hidden: true,
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'hunyuan-1.5',
      // No `duration`: Hunyuan 1.5 renders a fixed 121 frames (~5s) and its fal
      // schema has no duration field, so the node exposes no Duration control.
      parameters: { aspectRatio: '16:9' }
    },
  },

  'MinimaxVideo': {
    type: 'MinimaxVideo',
    camelCaseType: 'minimaxVideo',
    definition: NODE_DEFINITIONS['MinimaxVideo'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/minimax.svg',
    description: 'MiniMax Hailuo video — natural motion synthesis. Hailuo 2.3 default (Oct 2025), Hailuo 02 selectable.',
    hidden: true,
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'hailuo-2.3',
      parameters: { aspectRatio: '16:9', duration: '5' }
    },
  },
  
  'KlingAvatar': {
    type: 'KlingAvatar',
    camelCaseType: 'klingAvatar',
    definition: NODE_DEFINITIONS['KlingAvatar'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/kling.png',
    description: 'Lip sync from a portrait image + an audio track (Kling Avatar)',
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'kling-avatar',
      // No `duration`: the clip is exactly as long as the connected audio and
      // the model has no duration parameter, so the node exposes no control and
      // the billed seconds are measured from the audio instead.
      parameters: { aspectRatio: '16:9' }
    },
  },

  'GrokVideo': {
    type: 'GrokVideo',
    camelCaseType: 'grokVideo',
    definition: NODE_DEFINITIONS['GrokVideo'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/grok.svg',
    description: 'xAI Grok video model. Supports text-to-video and image-to-video with audio. Includes the 1.5-preview I2V leader (May 2026).',
    handlerType: 'xaiVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      // Use the xAI API model name as the canonical default — matches the
      // selectedModel options in nodeDefinitions.ts. Internal pricing key
      // 'grok-video' is mapped from this in NodeAdapters + xai.ts route.
      selectedModel: 'grok-imagine-video',
      parameters: { aspectRatio: '16:9', duration: '6' }
    },
  },

  'Seedance2Video': {
    type: 'Seedance2Video',
    camelCaseType: 'seedance2Video',
    definition: NODE_DEFINITIONS['Seedance2Video'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/seedance.svg',
    description: 'ByteDance Seedance 2 video generation via fal.ai. Pro, Fast and Mini tiers with native audio, plus first-to-last-frame.',
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'seedance-2-fast',
      parameters: { mode: 'image-to-video', aspectRatio: '16:9', duration: '6', resolution: '720p', enableAudio: true }
    },
  },

  'Seedance25Video': {
    type: 'Seedance25Video',
    camelCaseType: 'seedance25Video',
    definition: NODE_DEFINITIONS['Seedance25Video'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/seedance.svg',
    description: 'ByteDance Seedance 2.5 via fal.ai. Multi-shot storytelling with native audio. T2V, I2V and first-to-last-frame at 480p/720p.',
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'seedance-2.5',
      parameters: { mode: 'image-to-video', aspectRatio: '16:9', resolution: '720p', duration: '5', enableAudio: true }
    },
  },

  // MiniMax H3 (Hailuo 03) via fal.ai. Separate from MinimaxVideo above, which
  // is the older hailuo-02 / hailuo-2.3 family on a different fal namespace.
  // No `enableAudio` in initialData: H3's audio is mandatory and has no toggle.
  'MinimaxH3Video': {
    type: 'MinimaxH3Video',
    camelCaseType: 'minimaxH3Video',
    definition: NODE_DEFINITIONS['MinimaxH3Video'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/minimax.svg',
    description: 'MiniMax H3 (Hailuo 03) via fal.ai. 5-15s at 2K/24fps with native stereo audio. Text-to-video, image-to-video and first-to-last-frame.',
    handlerType: 'falAIVideo',
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'minimax-h3',
      parameters: { mode: 'image-to-video', aspectRatio: '16:9', resolution: '2K', duration: '5' }
    },
  },

  // ============ AUDIO NODES (ElevenLabs) ============
  // Audio-output nodes (outputsMediaType 'audio'). They live in the dedicated
  // 'audio' palette category (emerald), matching the SERVER registry which also
  // marks them 'audio' for correct per-generation cost estimation.
  'VoiceTTS': {
    type: 'VoiceTTS',
    camelCaseType: 'voiceTts',
    definition: NODE_DEFINITIONS['VoiceTTS'],
    category: 'audio',
    icon: RecordVoiceOverIcon,
    logoPath: '/icons/models/elevenlabs.svg',
    description: 'ElevenLabs text-to-speech. Turn text into natural spoken audio. Default: Eleven v3.',
    handlerType: 'audioGen',
    initialData: {
      prompt: '',
      selectedModel: 'eleven_v3',
      parameters: {
        voice: 'JBFqnCBsd6RMkjVDRZzb',
        format: 'mp3_44100_128',
        stability: 0.5,
        similarityBoost: 0.75,
      },
    },
  },

  'SoundEffects': {
    type: 'SoundEffects',
    camelCaseType: 'soundEffects',
    definition: NODE_DEFINITIONS['SoundEffects'],
    category: 'audio',
    icon: GraphicEqIcon,
    logoPath: '/icons/models/elevenlabs.svg',
    description: 'ElevenLabs sound effects. Generate a sound from a text description.',
    handlerType: 'audioGen',
    initialData: {
      prompt: '',
      parameters: { duration: 'auto', promptInfluence: 0.3, format: 'mp3_44100_128' },
    },
  },

  'Music': {
    type: 'Music',
    camelCaseType: 'music',
    definition: NODE_DEFINITIONS['Music'],
    category: 'audio',
    icon: MusicNoteIcon,
    logoPath: '/icons/models/elevenlabs.svg',
    description: 'ElevenLabs Music. Compose a song or instrumental track from a text prompt.',
    handlerType: 'audioGen',
    initialData: {
      prompt: '',
      selectedModel: 'music_v2',
      parameters: { duration: '30', format: 'mp3_44100_128' },
    },
  },

  // ============ HEYGEN NODES (via fal.ai) ============
  'HeyGenAvatarV3': {
    type: 'HeyGenAvatarV3',
    camelCaseType: 'heyGenAvatarV3',
    definition: NODE_DEFINITIONS['HeyGenAvatarV3'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/heygen.svg',
    description: 'HeyGen Avatar 3 — create talking avatar videos from a script.',
    handlerType: 'heygenVideo',
    hidden: true, // Temporarily hidden pending API validation
    hasMultipleInputHandles: false,
    initialData: {
      prompt: '',
      selectedModel: 'heygen-digital-twin-v3',
      parameters: { avatar: 'Adriana SuitSofa Front', voice: 'Warm Pro Narrator', duration: '30', resolution: '720p' },
    },
  },

  'HeyGenAvatarV4': {
    type: 'HeyGenAvatarV4',
    camelCaseType: 'heyGenAvatarV4',
    definition: NODE_DEFINITIONS['HeyGenAvatarV4'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/heygen.svg',
    description: 'HeyGen Avatar 4 — premium ultra-realistic talking avatar (Engine IV).',
    handlerType: 'heygenVideo',
    hidden: true, // Temporarily hidden pending API validation
    hasMultipleInputHandles: false,
    initialData: {
      prompt: '',
      selectedModel: 'heygen-digital-twin-v4',
      parameters: { avatar: 'Adriana SuitSofa Front', voice: 'Warm Pro Narrator', duration: '30', resolution: '720p' },
    },
  },

  // HeyGen Avatar V — current flagship digital-twin tier on fal.ai (175+ language lip-sync).
  // Endpoint fal-ai/heygen/avatar5/digital-twin, $0.10/sec verified on fal.ai.
  'HeyGenAvatarV': {
    type: 'HeyGenAvatarV',
    camelCaseType: 'heyGenAvatarV',
    definition: NODE_DEFINITIONS['HeyGenAvatarV'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/heygen.svg',
    description: 'HeyGen Avatar V — flagship digital twin with 175+ language phoneme-level lip-sync.',
    handlerType: 'heygenVideo',
    hidden: true, // Temporarily hidden pending API validation, in line with other HeyGen nodes
    hasMultipleInputHandles: false,
    initialData: {
      prompt: '',
      selectedModel: 'heygen-digital-twin-v5',
      parameters: { avatar: 'Adriana SuitSofa Front', voice: 'Warm Pro Narrator', duration: '30', resolution: '720p' },
    },
  },

  'HeyGenVideoAgent': {
    type: 'HeyGenVideoAgent',
    camelCaseType: 'heyGenVideoAgent',
    definition: NODE_DEFINITIONS['HeyGenVideoAgent'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/heygen.svg',
    description: 'HeyGen Video Agent — describe a video in natural language and AI generates it automatically.',
    handlerType: 'heygenVideo',
    hidden: true, // Temporarily hidden pending API validation
    hasMultipleInputHandles: false,
    initialData: {
      prompt: '',
      selectedModel: 'heygen-video-agent',
      parameters: { orientation: 'landscape', duration: '60' },
    },
  },

  'HeyGenTranslatePrecision': {
    type: 'HeyGenTranslatePrecision',
    camelCaseType: 'heyGenTranslatePrecision',
    definition: NODE_DEFINITIONS['HeyGenTranslatePrecision'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/heygen.svg',
    description: 'HeyGen Translate Precision — high-quality AI video translation and dubbing.',
    handlerType: 'heygenVideo',
    hidden: true, // Temporarily hidden pending API validation
    hasMultipleInputHandles: false,
    initialData: {
      prompt: '',
      selectedModel: 'heygen-translate-precision',
      parameters: { outputLanguage: 'Spanish', duration: '60' },
    },
  },

  'HeyGenTranslateSpeed': {
    type: 'HeyGenTranslateSpeed',
    camelCaseType: 'heyGenTranslateSpeed',
    definition: NODE_DEFINITIONS['HeyGenTranslateSpeed'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/heygen.svg',
    description: 'HeyGen Translate Speed — fast AI video translation and dubbing.',
    handlerType: 'heygenVideo',
    hidden: true, // Temporarily hidden pending API validation
    hasMultipleInputHandles: false,
    initialData: {
      prompt: '',
      selectedModel: 'heygen-translate-speed',
      parameters: { outputLanguage: 'Spanish', duration: '60' },
    },
  },

  'HeyGenPhotoVideo': {
    type: 'HeyGenPhotoVideo',
    camelCaseType: 'heyGenPhotoVideo',
    definition: NODE_DEFINITIONS['HeyGenPhotoVideo'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/heygen.svg',
    description: 'HeyGen Photo Video — animate any portrait photo into a talking avatar.',
    handlerType: 'heygenVideo',
    hidden: true, // Temporarily hidden pending API validation
    hasMultipleInputHandles: true,
    initialData: {
      prompt: '',
      selectedModel: 'heygen-image-to-video',
      parameters: { voice: 'Warm Pro Narrator', duration: '30', resolution: '720p', talkingStyle: 'stable' },
    },
  },

  'HeyGenLipsync': {
    type: 'HeyGenLipsync',
    camelCaseType: 'heyGenLipsync',
    definition: NODE_DEFINITIONS['HeyGenLipsync'],
    category: 'video',
    icon: VideocamIcon,
    logoPath: '/icons/models/heygen.svg',
    description: 'HeyGen Lipsync — replace a video\'s audio and automatically re-sync the lips. Speed or Precision variant.',
    handlerType: 'heygenVideo',
    hasMultipleInputHandles: true, // accepts both video + audio
    initialData: {
      prompt: '',
      selectedModel: 'heygen-lipsync-speed',
      parameters: {
        lipsyncVariant: 'heygen-lipsync-speed',
        duration: '30',
        enableDynamicDuration: true,
        enableSpeechEnhancement: false,
        disableMusicTrack: false,
        enableCaption: false,
      },
    },
  },

  // ============ UTILITY (input/output containers) ============
  'Output': {
    type: 'Output',
    camelCaseType: 'customOutput',
    definition: NODE_DEFINITIONS['Output'],
    category: 'utility',
    icon: OutputIcon,
    description: 'Display and manage generated content',
    handlerType: 'none',
    hasMultipleInputHandles: false, // Single input that accepts any type
    defaultWidth: 480,
    defaultHeight: 600,
    initialData: { content: '', context: undefined, type: undefined, pages: [], currentPageIndex: 0 },
  },

  'ImagePanel': {
    type: 'ImagePanel',
    camelCaseType: 'imagePanel',
    definition: NODE_DEFINITIONS['ImagePanel'],
    category: 'utility',
    icon: PhotoLibraryIcon,
    description: 'Container for images - drag & drop or paste',
    handlerType: 'none',
    defaultWidth: 420,
    defaultHeight: 300,
    initialData: { imageUrl: undefined, imageFile: undefined },
  },

  'DocumentPanel': {
    type: 'DocumentPanel',
    camelCaseType: 'documentPanel',
    definition: NODE_DEFINITIONS['DocumentPanel'],
    category: 'utility',
    icon: TextFieldsIcon,
    description: 'Prompt container',
    handlerType: 'none',
    initialData: { content: '', pages: [], currentPageIndex: 0 },
  },
  
  // ============ UTILITY NODES ============
  'Inpaint': {
    type: 'Inpaint',
    camelCaseType: 'inpaint',
    definition: NODE_DEFINITIONS['Inpaint'],
    category: 'utility',
    icon: BrushIcon,
    description: 'Edit images using AI inpainting',
    handlerType: 'none',
    hasMultipleInputHandles: true,
    hidden: true,
    defaultWidth: 360,
    defaultHeight: 420,
    initialData: { imageUrl: undefined, mask: undefined, prompt: '', inpaintResult: undefined },
  },
  
  'VideoTrim': {
    type: 'VideoTrim',
    camelCaseType: 'videoTrim',
    definition: NODE_DEFINITIONS['VideoTrim'],
    category: 'utility',
    icon: VideocamIcon,
    description: 'Cut a segment from a video by setting start and end times. Free, runs in browser.',
    handlerType: 'videoProcess',
    hidden: true,
    initialData: {
      parameters: { startTime: 0, endTime: 5 },
    },
  },

  'VideoSplit': {
    type: 'VideoSplit',
    camelCaseType: 'videoSplit',
    definition: NODE_DEFINITIONS['VideoSplit'],
    category: 'utility',
    icon: VideocamIcon,
    description: 'Split a video into multiple equal segments. Free, runs in browser.',
    handlerType: 'videoProcess',
    hidden: true,
    initialData: {
      parameters: { splitMode: 'equal', numParts: 2, segmentDuration: 3 },
    },
  },

  'VideoScale': {
    type: 'VideoScale',
    camelCaseType: 'videoScale',
    definition: NODE_DEFINITIONS['VideoScale'],
    category: 'utility',
    icon: VideocamIcon,
    description: 'Resize or AI-enhance videos. Free resize or AI upscale with credits.',
    handlerType: 'videoProcess',
    initialData: {
      parameters: { mode: 'resize', targetResolution: '1080p', crop: 'none' },
    },
  },

  'VideoStitch': {
    type: 'VideoStitch',
    camelCaseType: 'videoStitch',
    definition: NODE_DEFINITIONS['VideoStitch'],
    category: 'utility',
    icon: VideocamIcon,
    description: 'Concatenate multiple videos into one. Connect 2+ video sources.',
    handlerType: 'videoProcess',
    initialData: {
      parameters: { outputResolution: 'match-first' },
    },
  },

  'ImageScale': {
    type: 'ImageScale',
    camelCaseType: 'imageScale',
    definition: NODE_DEFINITIONS['ImageScale'],
    category: 'utility',
    icon: ImageIcon,
    description: 'Resize or AI-enhance images with Lanczos or ESRGAN upscaling.',
    handlerType: 'imageProcess',
    initialData: {
      parameters: { mode: 'resize', aspectRatio: 'original', resolution: 'original', targetWidth: 1920, targetHeight: 1080, fitMode: 'crop', cropAnchor: 'center' },
    },
  },

  'Dictation': {
    type: 'Dictation',
    camelCaseType: 'dictation',
    definition: NODE_DEFINITIONS['Dictation'],
    category: 'text',
    icon: MicIcon,
    description: 'Transcript note',
    hidden: false,
    handlerType: 'dictation',
    initialData: {},
  },
};

// ============ HELPER FUNCTIONS ============

/**
 * Get all nodes for a specific category
 */
export function getNodesByCategory(category: NodeCategory): NodeRegistration[] {
  return Object.values(NODE_REGISTRY)
    .filter(node => node.category === category && !node.hidden);
}

/**
 * Get all generator node camelCase types (image + video + audio categories)
 * These are nodes that produce media (images/videos/audio) and should have
 * an Output Container auto-inserted when connected to another generator.
 */
export function getGeneratorNodeTypes(): string[] {
  return Object.values(NODE_REGISTRY)
    .filter(node => node.category === 'image' || node.category === 'video' || node.category === 'audio')
    .map(node => node.camelCaseType);
}

/**
 * Check if a node type (camelCase) is a generator node
 */
export function isGeneratorNode(camelCaseType: string | undefined): boolean {
  if (!camelCaseType) return false;
  const registration = getNodeByReactFlowType(camelCaseType);
  if (!registration) return false;
  return registration.category === 'image' || registration.category === 'video' || registration.category === 'audio';
}

/**
 * Passive nodes hold or display content but do not transform it. Connecting two
 * passive nodes directly (e.g. Output -> Output, ImagePanel -> Output, etc.)
 * produces no useful work — a generator or processor must sit between them.
 *
 * Keep this list intentional: utility nodes that DO transform media
 * (VideoStitch, VideoScale, VideoTrim, VideoSplit, ImageScale, Inpaint) are
 * NOT passive — they are valid bridges between two containers.
 */
const PASSIVE_NODE_TYPES: ReadonlySet<string> = new Set([
  'customOutput',
  'universalOutput',
  'imagePanel',
  'documentPanel',
  'dictation',
]);

export function isPassiveNode(camelCaseType: string | undefined): boolean {
  if (!camelCaseType) return false;
  return PASSIVE_NODE_TYPES.has(camelCaseType);
}

/**
 * Get node registration by type (PascalCase)
 */
export function getNodeRegistration(type: string): NodeRegistration | undefined {
  return NODE_REGISTRY[type];
}

/**
 * Get node registration by camelCase type
 */
export function getNodeByReactFlowType(camelCaseType: string): NodeRegistration | undefined {
  return Object.values(NODE_REGISTRY)
    .find(node => node.camelCaseType === camelCaseType);
}

/**
 * Node types whose input handles change shape in first-last-frame mode.
 * camelCase (ReactFlow) types.
 *
 * GeminiOmni is deliberately absent: Google's Omni model has no frame
 * interpolation, so GEMINI_OMNI_NODE_DEF never offers `first-last-frame`.
 * Listing it here produced three phantom handles on any old flow that still
 * had the retired mode saved in `parameters.mode`.
 */
const FIRST_LAST_FRAME_NODE_TYPES = new Set(['veoVideo', 'klingO3Video', 'klingVideo', 'seedance25Video', 'seedance2Video', 'minimaxH3Video']);

export function isFirstLastFrameMode(camelCaseType: string, nodeData?: any): boolean {
  return FIRST_LAST_FRAME_NODE_TYPES.has(camelCaseType)
    && nodeData?.parameters?.mode === 'first-last-frame';
}

/**
 * Can this node type offer first-last-frame mode at all (regardless of the
 * mode currently stored in its data)?
 *
 * Accepts either casing: the camelCase (ReactFlow) type used by the set above,
 * or the PascalCase definition type the renderer has on hand. `FIRST_LAST_FRAME_NODE_TYPES`
 * is the single source of truth — UniversalNode used to carry a second,
 * hand-maintained PascalCase list for its edge migration, so adding a node to
 * one list without the other produced handles whose edges never migrated (or
 * migrated edges on handles that never rendered).
 */
export function isFirstLastFrameCapable(type: string): boolean {
  if (FIRST_LAST_FRAME_NODE_TYPES.has(type)) return true;
  const camel = NODE_REGISTRY[type]?.camelCaseType;
  return !!camel && FIRST_LAST_FRAME_NODE_TYPES.has(camel);
}

/**
 * Can this MODEL actually execute first-last-frame? Node-type capability is not
 * enough — the unified Kling node offers four model tiers and only 3.0 has an
 * FFLF path.
 *
 * Mirrors the two branches in `server/routes/falai.ts`: the endpoint override
 * (`veo-3.1` → the dedicated FFLF endpoint; `kling-3.0`/`kling-o3`, every
 * `seedance-2*` tier and `minimax-h3` → their own i2v endpoint) and the
 * input-shape branch (`image_url` + `end_image_url` for that same group,
 * `first_frame_url` + `last_frame_url` for VEO). Any other model falls through
 * to the plain text-to-video endpoint while still carrying frame params — a
 * failed request charged against reserved credits. Keep this in sync with those
 * two branches; there is no default case server-side.
 *
 * The `seedance-2*` / `minimax-h3` arms are not optimism: `end_image_url` is a
 * real, honoured field on each of those five image-to-video endpoints, verified
 * against fal's OpenAPI oracle
 * (GET https://fal.ai/api/openapi/queue/openapi.json?endpoint_id=<id>, which
 * 404s on a bogus id, so a 200 is proof rather than an echo). Presence alone is
 * not the test — the deprecated `fal-ai/bytedance/seedance/v1/lite/*` endpoints
 * also list `end_image_url` but document it as "accepted for backwards
 * compatibility and ignored". All five here carry a live description ("the
 * video will transition from the starting image to this ending image" /
 * "for first-to-last keyframe generation").
 *
 * Unknown/absent model ids return true: nodes whose FFLF does not route through
 * falai must not be blocked by a check written for the fal path.
 */
export function modelSupportsFirstLastFrame(modelId?: string | null): boolean {
  if (!modelId) return true;
  return modelId.includes('veo-3.1')
    || modelId.includes('kling-3.0')
    || modelId.includes('kling-o3')
    // Matches server/routes/falai.ts exactly: `startsWith`, not `includes`, so
    // the four tiers (2.5, 2 standard/fast/mini) are covered without matching
    // an unrelated id that merely contains the substring.
    || modelId.startsWith('seedance-2')
    || modelId === 'minimax-h3';
}

/**
 * Check if a node type renders one input handle per accepted media type.
 *
 * `registration.hasMultipleInputHandles` can only ever *disable* the split — it
 * cannot manufacture handles the renderer will not draw. A node that accepts a
 * single media type has exactly one input handle regardless of the flag.
 *
 * Minimax is why this matters: its definition dropped to
 * `acceptsMediaTypes: ['text']` while the registration kept
 * `hasMultipleInputHandles: true`, so the registry advertised an `input-text`
 * handle that BaseNode never rendered. Edges were then saved pointing at a
 * handle id with no DOM node behind it.
 */
export function hasMultipleInputHandles(type: string): boolean {
  const registration = NODE_REGISTRY[type];
  if (!registration) return false;

  const derived = (registration.definition?.acceptsMediaTypes?.length ?? 0) > 1;
  return registration.hasMultipleInputHandles !== undefined
    ? registration.hasMultipleInputHandles && derived
    : derived;
}

/**
 * Input handle info for a node
 */
export interface InputHandleInfo {
  id: string;
  type: string; // 'text', 'image', 'video', 'any'
  label: string; // Caption rendered next to the handle
}

/**
 * Get all input handles for a node type (by camelCase type from ReactFlow)
 * Returns array of handle info with id and type
 * Optional nodeData parameter for mode-dependent handles (e.g., VEO3 first-last-frame)
 */
export function getInputHandlesForNodeType(camelCaseType: string, nodeData?: any): InputHandleInfo[] {
  const registration = getNodeByReactFlowType(camelCaseType);
  if (!registration) {
    // Unknown node type - assume single 'any' input
    return [{ id: 'input', type: 'any', label: 'ANY' }];
  }

  const def = registration.definition;

  // Special case: VideoStitch has dynamic labeled clip handles (not a regular hasInputHandle)
  if (camelCaseType === 'videoStitch') {
    const clipCount = Math.max(2, Math.min(6, parseInt(String(nodeData?.parameters?.clipCount ?? '2'), 10)));
    return Array.from({ length: clipCount }, (_, i) => ({
      id: `input-video-${i + 1}`,
      type: 'video',
      label: `CLIP ${i + 1}`,
    }));
  }

  if (def?.hasInputHandle === false) {
    return [];
  }

  // First-last-frame mode: text + two labeled image frames.
  if (isFirstLastFrameMode(camelCaseType, nodeData)) {
    return [
      { id: 'input-text', type: 'text', label: 'TEXT' },
      { id: 'input-image-first', type: 'image', label: 'FIRST FRAME' },
      { id: 'input-image-last', type: 'image', label: 'LAST FRAME' },
    ];
  }

  const acceptsMediaTypes = def?.acceptsMediaTypes || ['any'];

  // One handle per accepted media type
  if (hasMultipleInputHandles(registration.type)) {
    return acceptsMediaTypes.map(type => ({
      id: `input-${type}`,
      type,
      label: type.toUpperCase(),
    }));
  }

  // Single input handle
  const soleType = acceptsMediaTypes[0] || 'any';
  return [{ id: 'input', type: soleType, label: soleType.toUpperCase() }];
}

/**
 * Same as getInputHandlesForNodeType but keyed by the PascalCase definition
 * type, which is what the renderer has on hand. Exists so UniversalNode draws
 * exactly the handles the registry advertises — the two used to derive them
 * independently and drift apart.
 */
export function getInputHandlesForDefinitionType(pascalType: string, nodeData?: any): InputHandleInfo[] {
  const registration = NODE_REGISTRY[pascalType];
  if (!registration) return [{ id: 'input', type: 'any', label: 'ANY' }];
  return getInputHandlesForNodeType(registration.camelCaseType, nodeData);
}

/**
 * Media type each legacy/per-type handle id used to carry. Used to re-point an
 * edge when a node's handle layout changes underneath a saved flow.
 */
const HANDLE_ID_MEDIA_TYPES: Record<string, string> = {
  'input': 'any',
  'input-any': 'any',
  'input-text': 'text',
  'input-image': 'image',
  'input-video': 'video',
  'input-audio': 'audio',
  'input-image-first': 'image',
  'input-image-last': 'image',
};

/**
 * Reconcile an edge's saved `targetHandle` against the handles the target node
 * actually renders right now.
 *
 * `keep: false` means the edge can no longer be anchored anywhere and should be
 * dropped. `targetHandle: null` means the node's default (unnamed) handle —
 * that is what BaseNode renders for single-input nodes, so the canonical id
 * `input` always normalizes to null.
 *
 * This MUST run before any "does this handle exist?" filter. A flow saved while
 * a node advertised per-type handles stores e.g. `input-text`; once that node
 * collapses to one default handle, an existence check on its own deletes the
 * user's edge instead of re-pointing it.
 */
export function resolveTargetHandle(
  camelCaseType: string,
  nodeData: any,
  targetHandle: string | null | undefined,
): { keep: boolean; targetHandle: string | null } {
  if (!targetHandle) return { keep: true, targetHandle: null };

  const handles = getInputHandlesForNodeType(camelCaseType, nodeData);
  if (handles.length === 0) {
    // Node has no input handle at all (e.g. prompt-only nodes).
    return { keep: false, targetHandle: null };
  }

  const single = handles.length === 1;

  if (handles.some(h => h.id === targetHandle)) {
    return { keep: true, targetHandle: single ? null : targetHandle };
  }

  // Stale id — re-point it to a handle of the same media type when one exists.
  const staleType = HANDLE_ID_MEDIA_TYPES[targetHandle];
  if (!staleType) return { keep: false, targetHandle: null };

  if (single) {
    const only = handles[0];
    const compatible = staleType === 'any' || only.type === 'any' || only.type === staleType;
    return compatible ? { keep: true, targetHandle: null } : { keep: false, targetHandle: null };
  }

  const match = handles.find(h => h.type === staleType) || handles.find(h => h.type === 'any');
  return match ? { keep: true, targetHandle: match.id } : { keep: false, targetHandle: null };
}

/**
 * Find a compatible input handle on a target node for a given source type
 * Returns the handle id if found, null otherwise
 * Optional nodeData parameter for mode-dependent handles (e.g., VEO3 first-last-frame)
 */
export function findCompatibleInputHandle(
  targetNodeType: string, // camelCase type
  sourceType: string, // 'text', 'image', 'video', 'any'
  nodeData?: any // Optional node data for dynamic handle detection
): string | null {
  const handles = getInputHandlesForNodeType(targetNodeType, nodeData);
  
  // First, try to find an exact match
  const exactMatch = handles.find(h => h.type === sourceType);
  if (exactMatch) {
    return exactMatch.id;
  }
  
  // Then, try to find an 'any' handle that accepts anything
  const anyHandle = handles.find(h => h.type === 'any');
  if (anyHandle) {
    return anyHandle.id;
  }
  
  // If source is 'any', it can connect to any handle
  if (sourceType === 'any' && handles.length > 0) {
    return handles[0].id;
  }
  
  // No compatible handle found
  return null;
}

/**
 * Get handler type for a node
 */
export function getHandlerType(type: string): NodeHandlerType {
  return NODE_REGISTRY[type]?.handlerType ?? 'none';
}

/**
 * Get initial data for creating a new node.
 * Merges definition setting defaults into parameters so every setting
 * has an explicit value from the start — no fallback chain needed.
 */
export function getInitialNodeData(type: string): Record<string, unknown> {
  // Resolve BOTH conventions: NODE_REGISTRY is keyed by PascalCase type, but the
  // node-creation call sites (palette / context menu) pass the camelCase
  // ReactFlow type (e.g. 'klingVideo'). Looking up only NODE_REGISTRY[type] with
  // a camelCase key returned undefined → `{}`, so palette-spawned nodes got NO
  // initial parameters or selectedModel — which is why model-dependent settings
  // (Kling/LTX duration conditionalOptions) fell back to base options until the
  // model was re-selected. Fall back to the camelCase resolver like every other
  // registry consumer does.
  const reg = NODE_REGISTRY[type] || getNodeByReactFlowType(type);
  if (!reg) return {};

  const base = { ...reg.initialData };

  // Build parameter defaults from the definition's settings
  const definitionDefaults: Record<string, unknown> = {};
  if (reg.definition?.settings) {
    for (const setting of reg.definition.settings) {
      if (setting.default != null) {
        definitionDefaults[setting.id] = setting.default;
      }
    }
  }

  // Merge: definition defaults first, then initialData.parameters overrides
  const existingParams = (base.parameters ?? {}) as Record<string, unknown>;
  base.parameters = { ...definitionDefaults, ...existingParams };

  // Mirror the authoritative top-level selectedModel (what adapters read) into
  // parameters. `conditionalOptions`/`showWhen` fields that depend on
  // 'selectedModel' resolve against `node.data.parameters`, but selectedModel is
  // stored top-level at `node.data.selectedModel`. Without this, model-dependent
  // settings (e.g. Kling / LTX duration) resolve against the definition-default
  // model — or nothing — on spawn, only correcting themselves after the user
  // re-selects the model. Seeding from initialData.selectedModel makes them
  // resolve for the ACTUAL model immediately. Empty/absent selectedModel is left
  // untouched so any definition default already merged above survives.
  if (typeof base.selectedModel === 'string' && base.selectedModel !== '') {
    (base.parameters as Record<string, unknown>).selectedModel = base.selectedModel;
  }

  return base;
}

/**
 * Get default dimensions for a node type.
 * Most nodes are 448×448; overrides via defaultWidth/defaultHeight in registry.
 */
export function getNodeDimensions(camelCaseType: string): { width: number; height: number } {
  const reg = getNodeByReactFlowType(camelCaseType);
  return {
    width: reg?.defaultWidth ?? 448,
    height: reg?.defaultHeight ?? 448,
  };
}

/**
 * Spawn dimensions for output/media container nodes
 * ('customOutput' / 'universalOutput'), keyed by the content they will host.
 *
 * Single source of truth for EVERY code path that spawns an output node
 * (generation complete, insert-between-generators, job resumption, asset
 * drops, paste, inpaint, etc.) so they all appear at the same, proper size.
 *
 * Width always comes from the Output registry entry. Media content
 * (image / video / unknown) uses the full registry height; text and audio
 * use shorter heights since they render compact viewers.
 */
export function getOutputNodeDimensions(contentType?: string): { width: number; height: number } {
  const { width, height } = getNodeDimensions('customOutput');
  switch (contentType) {
    case 'text':
      return { width, height: 300 };
    case 'audio':
      return { width, height: 200 };
    default:
      return { width, height };
  }
}

/**
 * Get all node types for TypeScript type generation
 */
export function getAllCamelCaseTypes(): string[] {
  return Object.values(NODE_REGISTRY).map(node => node.camelCaseType);
}

/**
 * Category to display name mapping
 */
export const CATEGORY_DISPLAY_NAMES: Record<NodeCategory, string> = {
  text: 'Text Processors',
  image: 'Image Processors',
  video: 'Video Processors',
  audio: 'Audio Processors',
  container: 'Containers',
  utility: 'Utilities',
};

/**
 * Build NODE_GROUPS for palette from registry with display-friendly names
 */
export function buildNodeGroups(): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  
  // Initialize groups in display order
  for (const category of ['text', 'image', 'video', 'audio', 'container', 'utility'] as NodeCategory[]) {
    const displayName = CATEGORY_DISPLAY_NAMES[category];
    groups[displayName] = [];
  }
  
  for (const node of Object.values(NODE_REGISTRY)) {
    if (!node.hidden) {
      const displayName = CATEGORY_DISPLAY_NAMES[node.category];
      groups[displayName].push(node.type);
    }
  }
  
  // Remove empty groups
  for (const key of Object.keys(groups)) {
    if (groups[key].length === 0) {
      delete groups[key];
    }
  }
  
  return groups;
}

/**
 * Build NODE_ICONS map from registry
 */
export function buildNodeIcons(): Record<string, React.ComponentType> {
  const icons: Record<string, React.ComponentType> = {};
  for (const node of Object.values(NODE_REGISTRY)) {
    icons[node.type] = node.icon;
  }
  return icons;
}

/**
 * Build NODE_DESCRIPTIONS map from registry
 */
export function buildNodeDescriptions(): Record<string, string> {
  const descriptions: Record<string, string> = {};
  for (const node of Object.values(NODE_REGISTRY)) {
    descriptions[node.type] = node.description;
  }
  return descriptions;
}

/**
 * Convert PascalCase type to camelCase
 */
export function typeToReactFlowType(pascalType: string): string {
  const registration = NODE_REGISTRY[pascalType];
  if (registration) {
    return registration.camelCaseType;
  }
  // Fallback: standard camelCase conversion
  return pascalType.charAt(0).toLowerCase() + pascalType.slice(1);
}

/**
 * Convert camelCase type to PascalCase
 */
export function reactFlowTypeToType(camelType: string): string {
  const registration = getNodeByReactFlowType(camelType);
  if (registration) {
    return registration.type;
  }
  // Fallback: standard PascalCase conversion
  return camelType.charAt(0).toUpperCase() + camelType.slice(1);
}

// ============ EXPORTS FOR BACKWARD COMPATIBILITY ============

/**
 * NODE_GROUPS - For use in NodePalette and context menus
 * @deprecated Use buildNodeGroups() for dynamic generation
 */
export const NODE_GROUPS = buildNodeGroups();

/**
 * NODE_ICONS - Icon components for each node type
 * @deprecated Use buildNodeIcons() for dynamic generation
 */
export const NODE_ICONS = buildNodeIcons();

/**
 * NODE_DESCRIPTIONS - Descriptions for tooltips
 * @deprecated Use buildNodeDescriptions() for dynamic generation
 */
export const NODE_DESCRIPTIONS = buildNodeDescriptions();
